import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgbCollapseModule, NgbDropdownModule, NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';

import { UserRole } from '../../core/models/user.model';
import { AuthService } from '../../core/services/auth.service';
import { ClientLogoService } from '../../core/services/client-logo.service';
import { LanguageService } from '../../core/services/language.service';
import { ReferralApiService } from '../../core/services/api/referral-api.service';

import { CustomerEdit } from '../../features/admin/pages/customer-management/edit/edit';
import { ChangePasswordForm } from '../../features/profile/pages/change-password-form/change-password-form';
import { Profile } from '../../features/profile/pages/profile/profile';
import { HelpGuidesModal } from '../../shared/components/help-guides-modal/help-guides-modal';

/** TG-492: seções agrupadas do menu de perfil no desktop. */
export type ProfileMenuGroup = 'profile' | 'language' | 'dashboards' | 'settings';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    NgbDropdownModule,
    NgbCollapseModule,
    NgbTooltip,
    NgSelectModule,
    TranslocoPipe,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit {
  readonly authService = inject(AuthService);

  readonly transloco = inject(TranslocoService);

  readonly router = inject(Router);

  readonly modalService = inject(NgbModal);

  private readonly languageService = inject(LanguageService);

  private readonly clientLogoService = inject(ClientLogoService);

  readonly currentUser = this.authService.currentUser;

  readonly isAuthenticated = this.authService.isAuthenticated;

  readonly companyName = this.clientLogoService.companyName;

  readonly companyLogoUrl = this.clientLogoService.companyLogoUrl;

  readonly companyLogoLoadFailed = this.clientLogoService.companyLogoLoadFailed;

  readonly companyDisplayName = this.clientLogoService.companyDisplayName;

  isMenuCollapsed = true;

  /**
   * TG-492: grupos recolhíveis do menu de perfil (apenas desktop).
   * Todos iniciam recolhidos para reduzir o espaço ocupado pelo menu.
   */
  private readonly expandedProfileGroups = signal<ReadonlySet<ProfileMenuGroup>>(new Set<ProfileMenuGroup>());

  readonly languageOptions = [
    {
      value: 'en-US',
      label: 'English',
    },
    {
      value: 'es',
      label: 'Español',
    },
    {
      value: 'pt-BR',
      label: 'Português',
    },
  ];

  readonly isAdminLike = computed(() => {
    const user = this.currentUser();

    return Number(user?.role) === UserRole.Admin || Number(user?.role) === UserRole.Super;
  });

  readonly isSuper = computed(() => this.authService.isSuper());
  // TG-382 #21: "Meus Projetos" só para o Tomador de Crédito; demais veem "Projetos".
  readonly isTomadorCredito = computed(() => this.authService.isTomadorCredito());
  readonly isReferralEligible = signal(false);

  private readonly referralApiService = inject(ReferralApiService);

  readonly isLandingPage = signal(this.isLandingPageUrl(this.router.url));
  readonly isAdminOrSuper = computed(() => this.authService.isAdminOrSuper());
  readonly canViewDashboards = computed(() => this.authService.canViewDashboards());

  constructor() {
    effect(() => {
      const clientId = this.currentUser()?.clientId?.trim();

      this.clientLogoService.initialize(clientId);
    });

    effect(() => {
      if (this.isTomadorCredito() && this.isAuthenticated()) {
        this.referralApiService
          .getMe()
          .then((info) => this.isReferralEligible.set(info.isEligible))
          .catch(() => this.isReferralEligible.set(false));
      } else {
        this.isReferralEligible.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.isMenuCollapsed = true;

        this.isLandingPage.set(this.isLandingPageUrl(event.urlAfterRedirects));
      });
  }

  private isLandingPageUrl(url: string): boolean {
    return url === '/' || url.startsWith('/?');
  }

  onCompanyLogoError(): void {
    this.clientLogoService.handleLogoError();
  }

  /**
   * TG-492: indica se a seção do menu de perfil está expandida.
   */
  isProfileGroupExpanded(group: ProfileMenuGroup): boolean {
    return this.expandedProfileGroups().has(group);
  }

  /**
   * TG-492: expande/recolhe uma seção do menu de perfil.
   * As seções são independentes — o usuário pode abrir mais de uma
   * e chegar à visualização equivalente ao menu antigo.
   */
  toggleProfileGroup(group: ProfileMenuGroup): void {
    const groups = new Set(this.expandedProfileGroups());

    if (!groups.delete(group)) {
      groups.add(group);
    }

    this.expandedProfileGroups.set(groups);
  }

  /**
   * TG-492: ao fechar o dropdown, recolhe todas as seções para que
   * o menu sempre reabra no formato compacto.
   */
  onProfileMenuOpenChange(isOpen: boolean): void {
    if (isOpen) {
      return;
    }

    this.expandedProfileGroups.set(new Set<ProfileMenuGroup>());
  }

  openChangePasswordModal(): void {
    const userId = this.currentUser()?.id;

    if (!userId) {
      return;
    }

    const modalRef = this.modalService.open(ChangePasswordForm, {
      centered: true,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.userId.set(userId);
  }

  openEditEmpresa(): void {
    const clientId = this.currentUser()?.clientId?.trim();

    if (!clientId) {
      return;
    }

    const modalRef = this.modalService.open(CustomerEdit, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.customerId.set(clientId);

    modalRef.result
      .then((result) => {
        if (!result?.updated) {
          return;
        }

        /**
         * Fallback de sincronização ao fechar o modal
         * principal. O modal de logo já atualiza o
         * serviço imediatamente.
         */
        this.clientLogoService.refresh(clientId);
      })
      .catch(() => {
        // Modal fechado sem salvar.
      });
  }

  openHelpGuides(): void {
    const modalRef = this.modalService.open(HelpGuidesModal, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.isAdminOrSuper.set(this.isAdminOrSuper());
  }

  openProfileModal(): void {
    const userId = this.currentUser()?.id;

    if (!userId) {
      return;
    }

    const modalRef = this.modalService.open(Profile, {
      centered: true,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.userId.set(userId);
  }

  logout(): void {
    /**
     * Remove client-logo:{clientId} do localStorage
     * e limpa os signals exibidos no header.
     */
    this.clientLogoService.clear();

    this.authService.logout();
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToProjects(): void {
    this.router.navigate(['/projects']);
  }

  navigateToAdmin(): void {
    this.router.navigate(['/admin/users']);
  }

  setLanguage(lang: string | null): void {
    if (!lang || lang === this.transloco.activeLang()) {
      return;
    }

    this.languageService.setLanguage(lang);

    if (this.isAuthenticated()) {
      this.languageService.saveUserLanguage(lang);
    }
  }
}
